import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Phone, Mail, MessageSquare, Calendar, Plus, Clock, User, MapPin, TrendingUp } from 'lucide-react';
import { GoGitBranch } from "react-icons/go";
import nodata from '../../../assets/nodata.gif';
import CallWidget from '../../../components/common/CallWidget/CallWidget';
import LeadActionOperation from '../../../common/LeadActionOperation/LeadActionOperation';
import EnquiryActionOperation from './EnquiryActionOperation';
import Panels from '../../../components/common/Panels/Panels';
import { getCallLogt, getEnquiryActivities, getPhysiscalAppointmentWidgetList } from '../../../utils/enquiry';
import API_ENDPOINTS, { BASES } from '../../../config/apiEndpoints';
import { getSession } from '../../../getSession';

const EnquiryDetails = ({ enquiry, maskObject = val => val }) => {
    const [showCallWidget, setShowCallWidget] = useState(false);
    const [callTriggerRect, setCallTriggerRect] = useState(null);
    const [isActionOpen, setIsActionOpen] = useState(false);
    const [actionMode, setActionMode] = useState('');
    const [activeTab, setActiveTab] = useState('activities');
    const [activities, setActivities] = useState([]);
    const [callLogs, setCallLogs] = useState([]);
    const [showAllTabs, setShowAllTabs] = useState(false);
    const [showMoreDetails, setShowMoreDetails] = useState(false);
    const [customDetailMoreData, setCustomDetailMoreData] = useState([]);
    const [isCustomMoreLoading, setIsCustomMoreLoading] = useState(false);

    const resolveUrl = (url) => {
        if (!url) return 'https://kit19.com/assets/custom/img/LeadActivityIMG/Whatsapp.png';
        if (url.startsWith('http')) return url;
        // prepend host if relative path
        return `https:/kit19.com${url}`;
    }

    const getAvatarStyle = (name) => {
        const palettes = [
            { bg: '#eff6ff', text: '#3b82f6', border: '#dbeafe' }, // Blue
            { bg: '#ecfdf5', text: '#10b981', border: '#d1fae5' }, // Green
            { bg: '#fff1f2', text: '#f43f5e', border: '#ffe4e6' }, // Rose
            { bg: '#fffbeb', text: '#f59e0b', border: '#fef3c7' }, // Amber
            { bg: '#f5f3ff', text: '#8b5cf6', border: '#ede9fe' }, // Violet
            { bg: '#ecfeff', text: '#06b6d4', border: '#cffafe' }  // Cyan
        ];
        const charCode = (name || 'U').charCodeAt(0);
        return palettes[charCode % palettes.length];
    };

    const getInitials = (name) => {
        return (name || 'U').split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
    };

    const fetchPAWidgetList = async () => {
        try {
            const response = await getPhysiscalAppointmentWidgetList(enquiry.EnquiryId, "Enquiry");
            console.log(`response of PA`, response)
        } catch (error) {
            console.error('fetchPAWidgetList error:', error);
        }
    }


    const fetchActivities = async () => {
        const payload = {
            EnquiryID: enquiry.EnquiryId,
            Start: 0,
            Limit: 10
        }
        try {
            const response = await getEnquiryActivities(payload)
            console.log(`response`, response)
            setActivities(response?.Details || response?.d)
        } catch (error) {
            console.error('fetchActivities error:', error);
            setActivities([]);
        }
    }
    const fetchCallLog = async () => {
        try {
            const response = await getCallLogt("Enquiry", enquiry.EnquiryId, 0, 10)
            console.log(`response callog`, response)

            // Parse the stringified JSON from response.d
            if (response && response.d) {
                const parsedData = JSON.parse(response.d);
                setCallLogs(Array.isArray(parsedData) ? parsedData : []);
            } else {
                setCallLogs([]);
            }
        } catch (error) {
            console.error('fetchCallLog error:', error);
            setCallLogs([]);
        }
    }

    const fetchCustomDataMore = async (id) => {
        try {
            setIsCustomMoreLoading(true);
            const SERVICE_AZURE_URL = (BASES.SERVICES_AZURE || '').replace(/\/$/, '');
            const Custome_Data_More = `${SERVICE_AZURE_URL}${API_ENDPOINTS.ENQUIRIES.CUSTOME_DATA_MORE}`;
            const { TokenId } = getSession();

            // Adding token to query string for GET request parity with backend expectations if any
            const url = `${Custome_Data_More}?enqid=${id}&TimeZoneName=India Standard Time&Token=${TokenId || ''}`;

            const response = await fetch(url);
            if (response.ok) {
                const result = await response.json();
                const details = result.Details || [];
                setCustomDetailMoreData(details);
            }
        } catch (error) {
            console.error("Error fetching custom data more:", error);
        } finally {
            setIsCustomMoreLoading(false);
        }
    };

    const parseCustomField = (text) => {
        if (!text) return { value: '', label: '', type: '' };
        const parts = text.split('|');
        if (parts.length === 3) {
            return { value: parts[0], label: parts[1], type: parts[2] };
        }
        return { value: '', label: text, type: 'v' };
    };

    useEffect(() => {
        if (enquiry) {
            fetchActivities();
            fetchCustomDataMore(enquiry.EnquiryId);
        }
    }, [enquiry]);

    // Fetch data when active tab changes
    useEffect(() => {
        if (!enquiry) return;

        if (activeTab === 'activities') {
            fetchActivities();
        } else if (activeTab === 'calls') {
            fetchCallLog();
        } else if (activeTab === 'Physical Appointments') {
            fetchPAWidgetList();
        }
        // Add more conditions for other tabs as needed
    }, [activeTab, enquiry]);

    if (!enquiry) {
        return (
            <div className="flex items-center justify-center h-full bg-[#ffffff]">
                <img src={nodata} alt="nodata" />
            </div>
        );
    }

    const maskedEnquiry = maskObject(enquiry);

    // const activities = [
    //     { type: 'call', time: '2 hours ago', desc: 'Follow-up call completed', user: 'You' },
    //     { type: 'note', time: '5 hours ago', desc: 'Interested in premium package', user: 'Vikram Singh' },
    //     { type: 'email', time: '1 day ago', desc: 'Sent product brochure', user: 'System' },
    //     { type: 'meeting', time: '2 days ago', desc: 'Initial consultation scheduled', user: 'You' }
    // ];

    return (
        <div className="flex-1 overflow-y-auto">
            <div className="max-w-5xl mx-auto p-6">
                {/* Header Section */}
                <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
                    <div className="flex items-start gap-4 mb-4">
                        {/* Profile Image */}
                        <div
                            className="w-20 h-20 rounded-full flex items-center justify-center border-2 border-gray-200 shadow-sm font-bold text-xl"
                            style={{
                                backgroundColor: getAvatarStyle(maskedEnquiry.PersonName).bg,
                                color: getAvatarStyle(maskedEnquiry.PersonName).text,
                                border: `1px solid ${getAvatarStyle(maskedEnquiry.PersonName).border}`
                            }}
                        >
                            {getInitials(maskedEnquiry.PersonName)}
                        </div>

                        <div className="flex-1">
                            <div className="flex justify-between items-start mb-2">
                                <div>
                                    <h1 className="text-[22px] font-semibold text-[#333] mb-1" style={{ fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif" }}>{maskedEnquiry.PersonName}</h1>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className={`premium-badge-dark ${String(maskedEnquiry.IsOpen).toLowerCase() === 'true' || maskedEnquiry.IsOpen === true ? 'badge-type-open' : 'badge-type-lead'}`}>
                                        {String(maskedEnquiry.IsOpen).toLowerCase() === 'true' || maskedEnquiry.IsOpen === true ? 'OPEN' : 'LEAD'}
                                    </span>
                                    <span className="text-sm text-gray-500">{maskedEnquiry.CreatedDate}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Quick Actions */}
                    <div className="flex gap-2">
                        <button
                            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                            onClick={(e) => {
                                e.stopPropagation();
                                const rect = e.currentTarget.getBoundingClientRect();
                                setCallTriggerRect({
                                    top: rect.top,
                                    left: rect.left,
                                    bottom: rect.bottom,
                                    right: rect.right,
                                    width: rect.width,
                                    height: rect.height
                                });
                                setShowCallWidget(true);
                            }}
                        >
                            <Phone className="w-4 h-4" />
                            CALL
                        </button>
                        <button
                            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
                            onClick={() => {
                                setActionMode('mail');
                                setIsActionOpen(true);
                            }}
                        >
                            <Mail className="w-4 h-4" />
                            Email
                        </button>
                        <button
                            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
                            onClick={() => {
                                setActionMode('sms');
                                setIsActionOpen(true);
                            }}
                        >
                            <MessageSquare className="w-4 h-4" />
                            SMS
                        </button>
                    </div>
                </div>

                <div className="grid grid-cols-3 gap-6">
                    {/* Main Content - 2 columns */}
                    <div className="col-span-2 space-y-6">
                        {/* Primary Information */}
                        <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-100">
                            <h2 className="text-sm font-bold text-slate-800 mb-6 flex items-center gap-2 border-b pb-3 uppercase tracking-wider">
                                <User className="w-4 h-4 text-blue-500" />
                                Enquiry Information
                            </h2>
                            <div className="grid grid-cols-2 gap-x-8 gap-y-6">
                                <div className="group">
                                    <label className="text-[11px] font-bold text-slate-400 uppercase tracking-widest block mb-1">Phone Number</label>
                                    <p
                                        className="text-[14px] text-slate-700 font-semibold cursor-pointer hover:text-blue-600 transition flex items-center gap-2"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            const rect = e.currentTarget.getBoundingClientRect();
                                            setCallTriggerRect({
                                                top: rect.top,
                                                left: rect.left,
                                                bottom: rect.bottom,
                                                right: rect.right,
                                                width: rect.width,
                                                height: rect.height
                                            });
                                            setShowCallWidget(true);
                                        }}
                                    >
                                        <Phone className="w-3.5 h-3.5 text-slate-300 group-hover:text-blue-500" />
                                        {maskedEnquiry.CsvMobileNo || 'N/A'}
                                    </p>
                                </div>
                                <div className="group">
                                    <label className="text-[11px] font-bold text-slate-400 uppercase tracking-widest block mb-1">Email Address</label>
                                    <p className="text-[14px] text-slate-700 font-semibold flex items-center gap-2">
                                        <Mail className="w-3.5 h-3.5 text-slate-300" />
                                        {maskedEnquiry.CsvEmailId || 'N/A'}
                                    </p>
                                </div>
                                <div>
                                    <label className="text-[11px] font-bold text-slate-400 uppercase tracking-widest block mb-1">Enquiry ID</label>
                                    <p className="text-[14px] text-slate-700 font-semibold bg-slate-50 px-2 py-1 rounded w-fit border border-slate-100">{maskedEnquiry.EnquiryId}</p>
                                </div>
                                <div>
                                    <label className="text-[11px] font-bold text-slate-400 uppercase tracking-widest block mb-1">Created Date</label>
                                    <p className="text-[14px] text-slate-700 font-semibold flex items-center gap-2">
                                        <Calendar className="w-3.5 h-3.5 text-slate-300" />
                                        {maskedEnquiry.CreatedDate}
                                    </p>
                                </div>
                                {(maskedEnquiry.Latitude && maskedEnquiry.Longitude) && (
                                    <div className="col-span-2">
                                        <label className="text-[11px] font-bold text-slate-400 uppercase tracking-widest block mb-1">Geographic Location</label>
                                        <p className="text-[14px] text-slate-700 font-semibold flex items-center gap-2">
                                            <MapPin className="w-3.5 h-3.5 text-slate-300" />
                                            {maskedEnquiry.Latitude}, {maskedEnquiry.Longitude}
                                        </p>
                                    </div>
                                )}
                                <div className="group">
                                    <label className="text-[11px] font-bold text-slate-400 uppercase tracking-widest block mb-1">Source</label>
                                    <p className="text-[14px] text-slate-700 font-semibold">{maskedEnquiry.SourceName || 'N/A'}</p>
                                </div>
                                <div className="group">
                                    <label className="text-[11px] font-bold text-slate-400 uppercase tracking-widest block mb-1">Medium</label>
                                    <p className="text-[14px] text-slate-700 font-semibold">{maskedEnquiry.MediumName || 'N/A'}</p>
                                </div>
                                <div className="group">
                                    <label className="text-[11px] font-bold text-slate-400 uppercase tracking-widest block mb-1">Campaign</label>
                                    <p className="text-[14px] text-slate-700 font-semibold">{maskedEnquiry.CampaignName || 'N/A'}</p>
                                </div>
                                <div className="col-span-2">
                                    <label className="text-[11px] font-bold text-slate-400 uppercase tracking-widest block mb-1">Remarks</label>
                                    <p className="text-[14px] text-slate-700 font-semibold leading-relaxed">{maskedEnquiry.Remarks || 'No remarks provided'}</p>
                                </div>
                            </div>

                            {/* More Details Toggle */}
                            <div style={{ textAlign: 'right', marginTop: '20px', borderTop: '1px solid #f1f5f9', paddingTop: '12px' }}>
                                <span
                                    onClick={() => setShowMoreDetails(!showMoreDetails)}
                                    style={{ cursor: 'pointer', color: '#2563eb', fontWeight: '600', fontSize: '13px', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '4px' }}
                                >
                                    {showMoreDetails ? 'Show Less' : 'Show More Details'}
                                    <TrendingUp className={`w-3.5 h-3.5 transition-transform ${showMoreDetails ? 'rotate-180' : ''}`} />
                                </span>
                            </div>

                            {showMoreDetails && (
                                <div style={{ marginTop: '20px', backgroundColor: '#f8fafc', borderRadius: '8px', padding: '16px', border: '1px solid #f1f5f9' }}>
                                    {isCustomMoreLoading ? (
                                        <div className="flex justify-center py-4">
                                            <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                                        </div>
                                    ) : customDetailMoreData.length > 0 ? (
                                        <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                                            {customDetailMoreData.map((item, idx) => {
                                                const field = parseCustomField(item.Text);
                                                return (
                                                    <div key={idx} className="group">
                                                        <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-0.5">{field.label}</label>
                                                        <p className="text-[13px] text-slate-600 font-medium">
                                                            {field.value || '-'}
                                                        </p>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <p className="text-[12px] text-slate-400 text-center py-2">No additional custom fields found.</p>
                                    )}
                                </div>
                            )}
                        </div>

                    </div>

                    {/* Activity History Sidebar */}
                    <div className="space-y-4">
                        <div className="bg-white rounded-lg shadow-sm p-4 h-[calc(100vh-320px)] flex flex-col border border-gray-100">
                            <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2 border-b pb-3 text-sm">
                                <Clock className="w-4 h-4 text-blue-500" />
                                Activity History
                            </h3>

                            <div className="flex-1 overflow-y-auto space-y-4 pr-1 custom-scrollbar">
                                {activities && activities.length > 0 ? (
                                    activities.map((activity, index) => (
                                        <div key={index} className="relative pl-2">
                                            {/* Timeline line */}
                                            {index !== activities.length - 1 && (
                                                <div className="absolute left-6 top-10 bottom-0 w-[1px] bg-gray-100"></div>
                                            )}

                                            <div className="flex gap-3">
                                                <div className="flex-shrink-0 relative z-10">
                                                    <div className="w-9 h-9 rounded-full bg-white border border-gray-100 flex items-center justify-center shadow-sm overflow-hidden p-1.5">
                                                        <img
                                                            src={resolveUrl(activity.EventType)}
                                                            alt="type"
                                                            className="w-full h-full object-contain"
                                                            onError={(e) => { e.target.src = 'https://kit19.com/assets/custom/img/LeadActivityIMG/email.png' }}
                                                        />
                                                    </div>
                                                </div>

                                                <div className="flex-1 min-w-0">
                                                    <div className="flex justify-between items-start mb-0.5">
                                                        <span className="text-[12px] font-bold text-gray-800 truncate" title={activity.EventName}>
                                                            {activity.EventName}
                                                        </span>
                                                    </div>
                                                    <div className="text-[11px] text-gray-400 mb-1.5 flex items-center gap-1">
                                                        <Calendar className="w-3 h-3" />
                                                        {activity.EventDate}
                                                    </div>

                                                    <div className="bg-[#f8fafc] rounded-lg p-2.5 border border-slate-100">
                                                        <p className="text-[11.5px] text-slate-600 leading-relaxed break-words">
                                                            {activity.EventDescription}
                                                        </p>
                                                    </div>

                                                    <div className="mt-2 flex items-center gap-1.5">
                                                        <div className="w-5 h-5 rounded-full bg-slate-100 flex items-center justify-center border border-slate-200">
                                                            <User className="w-3 h-3 text-slate-400" />
                                                        </div>

                                                        <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-tight">
                                                            {activity.UserLogin}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="flex flex-col items-center justify-center py-12 text-center">
                                        <div className="w-12 h-12 bg-gray-50 rounded-full flex items-center justify-center mb-3">
                                            <Clock className="w-6 h-6 text-gray-300" />
                                        </div>
                                        <p className="text-sm text-gray-400">No recent activities</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {showCallWidget && createPortal(
                <CallWidget
                    show={showCallWidget}
                    onClose={() => setShowCallWidget(false)}
                    toMobileNo={enquiry.CsvMobileNo}
                    entityId={enquiry.EnquiryId}
                    entityName="Enquiry"
                    personName={enquiry.PersonName}
                    triggerRect={callTriggerRect}
                />,
                document.body
            )}
            <Panels
                isOpen={isActionOpen}
                onClose={() => setIsActionOpen(false)}
                width="60%"
            >
                {['mail', 'send-mail', 'sms', 'send-sms'].includes(actionMode) ? (
                    <EnquiryActionOperation
                        entity={{
                            enquiryId: enquiry.EnquiryId,
                            MobileNo: enquiry.CsvMobileNo,
                            Email: enquiry.CsvEmailId || '',
                            PersonName: enquiry.PersonName
                        }}
                        mode={actionMode}
                        onBack={() => setIsActionOpen(false)}
                        onSuccess={() => setIsActionOpen(false)}
                    />
                ) : (
                    <LeadActionOperation
                        entity={{
                            enquiryId: enquiry.EnquiryId,
                            MobileNo: enquiry.CsvMobileNo,
                            Email: enquiry.CsvEmailId || '',
                            PersonName: enquiry.PersonName
                        }}
                        mode={actionMode}
                        onBack={() => setIsActionOpen(false)}
                        onSuccess={() => setIsActionOpen(false)}
                    />
                )}
            </Panels>
        </div>
    );
}

export default EnquiryDetails;
